import { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import api from "../../services/api";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import DnaOverlayLoader from "../loader/DnaOverlayLoader";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const AppointmentForm = ({ show, handleClose, doctorName }) => {
  const recaptcha = useGoogleReCaptcha();
  const [executeRecaptcha, setExecuteRecaptcha] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    patient_name: "",
    phone: "",
    date: "",
    query: "",
  });

  useEffect(() => {
    if (!show) {
      setExecuteRecaptcha(null);
      return;
    }

    const initRecaptcha = () => {
      if (recaptcha?.executeRecaptcha) {
        setExecuteRecaptcha(() => recaptcha.executeRecaptcha);
      }
    };

    if ("requestIdleCallback" in window) {
      requestIdleCallback(initRecaptcha);
    } else {
      setTimeout(initRecaptcha, 800);
    }
  }, [show, recaptcha]);

  const trackEvent = (eventName, params = {}) => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }
  };

  useEffect(() => {
    if (!show) {
      setFormData({
        patient_name: "",
        phone: "",
        date: "",
        query: "",
      });
    }
  }, [show]);

  const handleChange = (e) => {

    if (e.target.name === "patient_name") {
      e.target.value = e.target.value.replace(/[^a-zA-Z ]/g, "");
    }

    if (e.target.name === "phone") {
      e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!executeRecaptcha) {
      toast.error("reCAPTCHA not ready yet. Please try again.");
      return;
    }

    setLoading(true);

    if (formData.phone.length < 10) {
      toast.error("Please enter a 10-digit phone number");
      setLoading(false);
      return;
    }

    const payload = {
      ...formData,
      phone: `+91${formData.phone}`,
      doctor_name: doctorName,
      page_url: window.location.href
    };

    try {
      await api.post(`/api/send-appointment`, payload);
      setLoading(false);
      await Swal.fire({
        title: "Success!",
        text: "Appointment sent successfully!",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
        width: 450, 
      });
      trackEvent("appointment_form_submitted", { doctor_name: doctorName });
      handleClose();
    } catch (error) {
      toast.error("Failed to send appointment. Try again.");
      console.error("Send error:", error);
    }

    setLoading(false);
  };

  return (
    <>
      {loading && <DnaOverlayLoader />}

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Book Appointments
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Your Name *</Form.Label>
              <Form.Control
                type="text"
                name="patient_name"
                maxLength={50}
                required
                value={formData.patient_name}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone Number *</Form.Label>

              <div className="d-flex">
                <span
                  style={{
                    padding: "8px 12px",
                    background: "#eee",
                    border: "1px solid #ced4da",
                    borderRight: "none",
                    borderRadius: "4px 0 0 4px",
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  +91
                </span>

                <Form.Control
                  type="text"
                  name="phone"
                  maxLength={10}
                  minLength={10}
                  required
                  placeholder="Enter 10-digit number"
                  value={formData.phone}
                  onChange={handleChange}
                  style={{ borderRadius: "0 4px 4px 0" }}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Appointment Date *</Form.Label>
              <Form.Control
                type="date"
                name="date"
                required
                value={formData.date}
                onChange={handleChange}
                min={new Date().toISOString().split("T")[0]}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Query</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="query"
                maxLength={500}
                value={formData.query}
                onChange={handleChange}
              />
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="w-100"
              disabled={loading}
            >
              {loading ? "Sending..." : "Book Appointment"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AppointmentForm;
